import { useEffect, useState } from 'react'
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded'
import { NAV_ITEMS, type NavItem } from './nav'
import { pagesService } from '../services/pagesService'
import { eventService, type NavSummary } from '../services/eventService'

/**
 * The attendee navigation, resolved from three inputs:
 *
 *  1. Built-in items (`NAV_ITEMS`) — filtered by the combined rule below.
 *  2. Per-event admin nav settings (`visibility` auto/show/hide + `ordinal`).
 *  3. Content availability (does the section actually have anything to show).
 *  4. Admin-created custom pages that are published + "show in nav".
 *
 * Built-in visibility rule (per item key):
 *  - `hide`  → never shown.
 *  - `show`  → always shown (admin override, even when empty).
 *  - `auto`  → shown only when the section has content (empty → hidden).
 * Home is always shown; My badge / Network have no content signal so are treated
 * as always-having-content (still hideable via `hide`).
 */
export function useNavItems(): NavItem[] {
  const [items, setItems] = useState<NavItem[]>(NAV_ITEMS)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      eventService.getNavSummary(),
      pagesService.listPages().catch(() => []),
    ])
      .then(([summary, pages]) => {
        if (cancelled) return

        const builtins = resolveBuiltins(summary)

        const custom: NavItem[] = pages
          .filter((p) => !p.isHome && p.showInNav && p.published)
          .sort((a, b) => a.ordinal - b.ordinal)
          .map((p) => ({
            label: p.navLabel || p.title,
            sub: `p/${p.slug}`,
            icon: ArticleRoundedIcon,
            primary: false,
          }))

        setItems([...builtins, ...custom])
      })
      .catch(() => {
        /* keep built-in nav on failure */
      })
    return () => {
      cancelled = true
    }
  }, [])

  return items
}

function resolveBuiltins(summary: NavSummary): NavItem[] {
  const withOrder = NAV_ITEMS.map((item, index) => {
    const key = item.key ?? ''
    const setting = summary.settings[key]
    const visibility = setting?.visibility ?? 'auto'
    // Home is never content-gated; sections without a content signal default true.
    const hasContent =
      key === 'home' ? true : summary.content[key] ?? true

    let visible: boolean
    if (visibility === 'hide') visible = false
    else if (visibility === 'show') visible = true
    else visible = hasContent // 'auto'
    if (key === 'home') visible = true // Home is always present

    // Home is always first. Otherwise the admin-set ordinal wins; items without
    // a saved setting fall back to their natural position (offset so they sort
    // after admin-ordered items rather than tying with ordinal 0).
    let order: number
    if (key === 'home') order = -1
    else if (setting) order = setting.ordinal
    else order = 1000 + index

    return { item, visible, order }
  })

  return withOrder
    .filter((x) => x.visible)
    .sort((a, b) => a.order - b.order)
    .map((x) => x.item)
}
